import { useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Headset, LayoutGrid, Receipt, Search, SlidersHorizontal, X } from "lucide-react";
import { ResourceCard } from "@/components/resources/ResourceCard";
import { WhatsAppButton } from "@/components/ui/Cta";
import type { ResourceItem } from "@/data/content";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import {
  categoryIcon,
  categoryKey,
  matchesSearch,
  resourceCategories,
} from "@/components/resources/resourceCategories";

type PriceBand = "free" | "under20" | "20to50" | "over50";
type SortKey = "featured" | "priceAsc" | "priceDesc" | "title";

const PRICE_BANDS: { key: PriceBand; labelKey: string; test: (cents: number) => boolean }[] = [
  { key: "free", labelKey: "priceFree", test: (c) => c === 0 },
  { key: "under20", labelKey: "priceUnder20", test: (c) => c > 0 && c < 2000 },
  { key: "20to50", labelKey: "price20to50", test: (c) => c >= 2000 && c <= 5000 },
  { key: "over50", labelKey: "priceOver50", test: (c) => c > 5000 },
];

const SORTS: { key: SortKey; labelKey: string }[] = [
  { key: "featured", labelKey: "sortFeatured" },
  { key: "priceAsc", labelKey: "sortPriceAsc" },
  { key: "priceDesc", labelKey: "sortPriceDesc" },
  { key: "title", labelKey: "sortTitle" },
];

type Props = {
  items: ResourceItem[];
  onAccessRequest: (resource: ResourceItem) => void;
};

/** The store: category tabs, a filter sidebar (search, price), sorting and the card grid. */
export function ResourcesCatalog({ items, onAccessRequest }: Props) {
  const { t } = useAppTranslation("resourcesPage");
  const active = useMemo(() => items.filter((res) => !res.inactive), [items]);
  const categories = useMemo(() => resourceCategories(active), [active]);
  const [category, setCategory] = useState<string>("all");
  const [query, setQuery] = useState("");
  const [bands, setBands] = useState<Set<PriceBand>>(new Set());
  const [sort, setSort] = useState<SortKey>("featured");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const searchId = useId();
  const sortId = useId();
  const filtersId = useId();

  /* Only offer price bands that some resource falls into. */
  const availableBands = PRICE_BANDS.filter((band) => active.some((res) => band.test(res.priceCents)));

  const visible = useMemo(() => {
    const filtered = active.filter(
      (res) =>
        (category === "all" || categoryKey(res) === category) &&
        matchesSearch(res, query) &&
        (bands.size === 0 || PRICE_BANDS.some((band) => bands.has(band.key) && band.test(res.priceCents))),
    );
    if (sort === "featured") return filtered;
    return [...filtered].sort((a, b) => {
      if (sort === "priceAsc") return a.priceCents - b.priceCents;
      if (sort === "priceDesc") return b.priceCents - a.priceCents;
      return a.title.localeCompare(b.title);
    });
  }, [active, category, query, bands, sort]);

  const filtering = category !== "all" || query.trim() !== "" || bands.size > 0;
  const heading = category === "all" ? t("catalogTitle") : categories.find((c) => c.key === category)?.label;

  function toggleBand(key: PriceBand) {
    setBands((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function clearFilters() {
    setCategory("all");
    setQuery("");
    setBands(new Set());
  }

  const tabs = [{ key: "all", label: t("categoryAll") }, ...categories];

  return (
    <div>
      {categories.length > 1 ? (
        <nav aria-label={t("categoriesLabel")} className="-mx-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0">
          <ul className="flex min-w-max gap-3 lg:grid lg:min-w-0 lg:grid-cols-[repeat(auto-fit,minmax(8.5rem,1fr))]">
            {tabs.map((tab) => {
              const Icon = tab.key === "all" ? LayoutGrid : categoryIcon(tab.label);
              const selected = category === tab.key;
              return (
                <li key={tab.key}>
                  <button
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setCategory(tab.key)}
                    className={`flex h-full min-h-22 w-32 flex-col items-center justify-center gap-2 rounded-2xl border px-3 py-4 text-center text-sm font-semibold transition-[background-color,border-color,color,box-shadow] duration-300 lg:w-full ${
                      selected
                        ? "border-teal-strong/25 bg-teal-soft text-teal-strong shadow-lit"
                        : "border-brand-primary/8 bg-white text-brand-primary hover:border-brand-primary/20 hover:shadow-lit"
                    }`}
                  >
                    <Icon className="size-6" strokeWidth={1.5} aria-hidden="true" />
                    <span className="leading-tight">{tab.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      ) : null}

      <div className="mt-10 grid gap-8 lg:grid-cols-[15rem_1fr] lg:gap-10">
        <div className="lg:hidden">
          <button
            type="button"
            aria-expanded={filtersOpen}
            aria-controls={filtersId}
            onClick={() => setFiltersOpen((v) => !v)}
            className="inline-flex min-h-11 items-center gap-2 rounded-full border border-brand-primary/15 bg-white px-5 text-sm font-semibold text-brand-primary"
          >
            <SlidersHorizontal className="size-4" aria-hidden="true" />
            {t("showFilters")}
            <ChevronDown
              className={`size-4 transition-transform duration-300 ${filtersOpen ? "rotate-180" : ""}`}
              aria-hidden="true"
            />
          </button>
        </div>

        <aside
          id={filtersId}
          aria-label={t("filters")}
          className={`${filtersOpen ? "block" : "hidden"} space-y-8 lg:block`}
        >
          <div>
            <label htmlFor={searchId} className="text-sm font-semibold text-brand-primary">
              {t("searchLabel")}
            </label>
            <div className="relative mt-3">
              <Search
                className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-ink-muted"
                aria-hidden="true"
              />
              <input
                id={searchId}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("searchPlaceholder")}
                className="w-full rounded-full border border-brand-primary/15 bg-white py-3 pl-11 pr-4 text-base text-ink outline-none transition-colors placeholder:text-ink-muted focus:border-teal-strong focus:ring-2 focus:ring-brand-secondary/40"
              />
            </div>
          </div>

          {availableBands.length > 1 ? (
            <fieldset className="border-t border-brand-primary/8 pt-6">
              <legend className="float-left w-full text-sm font-semibold text-brand-primary">
                {t("priceFilter")}
              </legend>
              <ul className="clear-left space-y-1 pt-3">
                {availableBands.map((band) => (
                  <li key={band.key}>
                    <label className="flex min-h-10 items-center gap-3 text-base text-ink-soft">
                      <input
                        type="checkbox"
                        checked={bands.has(band.key)}
                        onChange={() => toggleBand(band.key)}
                        className="size-4.5 rounded border-brand-primary/30 accent-[var(--color-teal-strong)]"
                      />
                      {t(band.labelKey)}
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
          ) : null}

          {filtering ? (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 rounded-full text-sm font-semibold text-teal-strong underline-offset-4 hover:underline"
            >
              <X className="size-4" aria-hidden="true" />
              {t("clearFilters")}
            </button>
          ) : null}

          <div className="rounded-2xl bg-teal-soft p-6">
            <Headset className="size-7 text-teal-strong" strokeWidth={1.5} aria-hidden="true" />
            <p className="mt-3 font-display text-xl font-semibold text-brand-primary">{t("helpTitle")}</p>
            <p className="mt-2 text-base leading-normal text-ink-soft">{t("helpBody")}</p>
            <WhatsAppButton
              location="resources_help"
              variant="outline"
              size="md"
              label={t("helpCta")}
              message={t("helpTitle")}
              className="mt-5 !min-h-10"
            />
          </div>

          <Link
            to="/recursos/mis-compras"
            className="inline-flex items-center gap-2 rounded-full text-sm font-semibold text-brand-primary underline-offset-4 hover:underline"
          >
            <Receipt className="size-4 text-teal-strong" aria-hidden="true" />
            {t("myPurchases")}
          </Link>
        </aside>

        <section aria-labelledby="catalog-heading">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2
                id="catalog-heading"
                className="font-display text-3xl font-semibold leading-tight tracking-[-0.02em] text-brand-primary"
              >
                {heading}
              </h2>
              <p className="mt-1 text-sm text-ink-muted" aria-live="polite">
                {t("resultsCount", { count: visible.length })}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label htmlFor={sortId} className="text-sm text-ink-muted">
                {t("sortLabel")}
              </label>
              <div className="relative">
                <select
                  id={sortId}
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortKey)}
                  className="min-h-11 appearance-none rounded-xl border border-brand-primary/15 bg-white py-2 pl-4 pr-10 text-sm font-medium text-brand-primary outline-none focus:border-teal-strong focus:ring-2 focus:ring-brand-secondary/40"
                >
                  {SORTS.map((option) => (
                    <option key={option.key} value={option.key}>
                      {t(option.labelKey)}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-ink-muted"
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>

          {visible.length ? (
            <ul className="mt-8 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {visible.map((res, i) => (
                <ResourceCard key={res.id ?? res.title} resource={res} onAccessRequest={onAccessRequest} index={i} />
              ))}
            </ul>
          ) : (
            <div className="mt-8 rounded-[1.75rem] border border-dashed border-brand-primary/15 bg-white p-10 text-center">
              <p className="text-ink-soft">{active.length ? t("noMatches") : t("empty")}</p>
              {filtering ? (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="mt-4 text-sm font-semibold text-teal-strong underline-offset-4 hover:underline"
                >
                  {t("clearFilters")}
                </button>
              ) : null}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
